import type { ObjectMetadata } from '@olytic/foundation';
import {
  aggregateKey, formulaSummaries,
  type ReportDefinition, type ReportSchemaObject, type ReportServerData, type ReportType, reportTypesFromSchema,
} from '@olytic/foundation/reports';
import { useEffect, useState } from 'react';

import { objects } from '../config/objects';
import type { Row } from '../config/objects/kit';
import { humanize } from '../objects/format';
import { readWritable } from '../objects/writes';
import type { WritableByObject } from './reportEdits';

// Reporting follows the data model: the server reads every object a report can
// use from the database catalog (GET /api/reports/schema, reports.mjs), with
// its foreign keys as lookups, limited to what the viewer may read. Foundation's
// reportTypesFromSchema turns that into report types, parent and grandparent
// fields ("Account › Owner › Name") and child cross filters, so a new column
// or foreign key is reportable with no reporting code. The record pages'
// labels go over the server's, which it derives from column names.

type Fetch = (input: string, init?: RequestInit) => Promise<Response>;

/** The zone and week start every report's dates are bucketed and shown in. */
export const REPORT_TIME_ZONE = 'America/New_York';
export const REPORT_WEEK_STARTS_ON = 0;

/** The schema with each object's record-page names and field labels. */
export function labelSchema(schema: ReportSchemaObject[], metadata: Partial<Record<string, ObjectMetadata<Row>>> = objects): ReportSchemaObject[] {
  return schema.map((object) => {
    const page = metadata[object.key];
    const labels = new Map((page?.details ?? []).map((d) => [d.key, d.label]));
    return {
      ...object,
      ...(page ? { label: page.singular, pluralLabel: page.plural } : {}),
      fields: object.fields.map((field) => {
        const label = field.key === 'name' && page ? `${page.singular} Name` : labels.get(field.key) ?? field.label;
        const options = field.options?.map((o) => ({ value: o.value, label: humanize(o.value) }));
        const lookupLabel = field.lookup ? labels.get(`${field.lookup.key ?? ''}_name`) ?? field.lookup.label : undefined;
        return {
          ...field,
          label,
          ...(options ? { options } : {}),
          ...(field.lookup ? { lookup: { ...field.lookup, ...(lookupLabel ? { label: lookupLabel } : {}) } } : {}),
        };
      }),
    };
  });
}

export interface ReportSchema {
  objects: ReportSchemaObject[];
  /** Each object's writable fields: what a report may change in place (reportEdits.ts). */
  writable: WritableByObject;
}

export async function loadReportSchema(fetcher: Fetch, signal?: AbortSignal): Promise<ReportSchema> {
  const response = await fetcher('/api/reports/schema', signal ? { signal } : undefined);
  const body = (await response.json().catch(() => null)) as { objects?: ReportSchemaObject[]; writable?: unknown; error?: string } | null;
  if (!response.ok || !Array.isArray(body?.objects)) {
    throw new Error(body?.error ?? `The report objects could not be loaded (${response.status}).`);
  }
  const writable: WritableByObject = {};
  if (typeof body.writable === 'object' && body.writable !== null) {
    for (const [key, fields] of Object.entries(body.writable as Record<string, unknown>)) {
      writable[key] = readWritable({ writable: { fields } }).fields;
    }
  }
  return { objects: labelSchema(body.objects), writable };
}

export interface ReportTypes {
  types: ReportType[] | null;
  schema: ReportSchema | null;
  error: string | null;
}

// One read per page load: the schema changes only with a migration.
let loaded: Promise<{ types: ReportType[]; schema: ReportSchema }> | null = null;

/** Every report type the viewer may use, from the server's schema. */
export function useReportTypes(fetcher: Fetch): ReportTypes {
  const [state, setState] = useState<ReportTypes>({ types: null, schema: null, error: null });
  useEffect(() => {
    let live = true;
    loaded ??= loadReportSchema(fetcher).then((schema) => ({ types: reportTypesFromSchema(schema.objects), schema }));
    loaded.then(
      ({ types, schema }) => { if (live) setState({ types, schema, error: null }); },
      (error: unknown) => {
        loaded = null;
        if (live) setState({ types: null, schema: null, error: error instanceof Error ? error.message : 'The report objects could not be loaded.' });
      },
    );
    return () => { live = false; };
  }, [fetcher]);
  return state;
}

/** What POST /api/reports/run answers: Foundation's ReportServerData, and the server's row cap when it cut the rows. */
export interface ServerRun extends ReportServerData {
  rowCap?: number;
}

/**
 * The definition the server runs: the report's own summaries, then the ones
 * its formulas read and it does not already show, each `fn` and `field` once
 * (Foundation's aggregate key). Formulas are worked out in the browser from
 * the summaries the server returns, and the server computes only those named,
 * so it must be asked for every formula input. The server ignores `formulas`.
 * A definition with no formula inputs to add is sent as it is.
 */
export function serverDefinition(definition: ReportDefinition): ReportDefinition {
  const seen = new Set(definition.summaries.map((s) => aggregateKey(s.fn, s.field)));
  const extra = formulaSummaries(definition).filter((s) => {
    const key = aggregateKey(s.fn, s.field);
    if (seen.has(key)) return false;
    seen.add(key);
    return true;
  });
  return extra.length > 0 ? { ...definition, summaries: [...definition.summaries, ...extra] } : definition;
}

/**
 * Runs a definition on the server, with its formulas' inputs added
 * (serverDefinition). With `cell` (a matrix cell's group values), the cell's
 * records only, a page from `offset`.
 */
export async function runReportOnServer(
  fetcher: Fetch,
  definition: ReportDefinition,
  signal?: AbortSignal,
  drill?: { cell: { rows: unknown[]; columns: unknown[] }; offset: number },
): Promise<ServerRun> {
  const response = await fetcher('/api/reports/run', {
    method: 'POST',
    headers: { 'content-type': 'application/json' },
    body: JSON.stringify({ definition: serverDefinition(definition), ...(drill ?? {}) }),
    ...(signal ? { signal } : {}),
  });
  const body = (await response.json().catch(() => null)) as (ServerRun & { error?: string }) | null;
  if (!response.ok || !Array.isArray(body?.rows) || !Array.isArray(body?.summaries)) {
    throw new Error(body?.error ?? `The report could not be run (${response.status}).`);
  }
  return body;
}
